import { useCallback, useEffect, useMemo, useState } from 'react';
import { Box, Chip, Paper, Stack, ToggleButton, ToggleButtonGroup, Typography } from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import NoteIcon from '@mui/icons-material/StickyNote2';
import { IconButton } from '@mui/material';
import { ACCENTS, readableInk } from '../../lib/theme';
import { MapLink } from '../../components/MapLink';
import { addDays, endOfMonth, formatMonthYear, formatShort, startOfMonth, today, type IsoDate } from '../../lib/dates';
import { fetchDay, fetchHome, fetchMonth, type DayBooking, type MyDay } from '../../lib/employee';
import { EmployeeShell } from './shell';

/**
 * The employee calendar — a port of
 * `views/backend/employeedashboard/calendar.php` (1,144 lines, 389 of them CSS).
 *
 * The legacy screen had a month grid whose event dots never worked: `init_calendar`
 * called `check_events()`, which read an `event_data` object initialised to
 * `{"events": []}` and never written to. Every date rendered plain, so the grid
 * told you nothing and you had to tap each day to find out. Here one request per
 * month marks the days that have bookings, which is the only reason to draw a
 * month grid at all.
 *
 * Two behaviours are carried over as they are, because they are staff settings
 * the office controls (staff.mobile_calendar_show_all / mobile_contractor_view):
 *
 *   * The day list is either just this person's jobs or everyone's. The server
 *     decides from the flag; it cannot be widened from the browser.
 *   * The Calendar | Timesheet toggle. In the legacy code it did one thing:
 *     reveal a per-job hours figure. Same here, and the figure is now real
 *     hours from the timesheet rather than the legacy `staffHoursData`, which
 *     took `[0]->total_hours` of the day's rows — the first entry's hours, not
 *     the job's.
 */

const WEEKDAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'] as const;

/** The Monday-first grid for a month, padded with the surrounding days. */
function monthGrid(anchor: IsoDate): IsoDate[] {
    const first = startOfMonth(anchor);
    const last = endOfMonth(anchor);
    // getUTCDay gives 0 for Sunday; the grid starts on Monday.
    const lead = (new Date(`${first}T00:00:00Z`).getUTCDay() + 6) % 7;
    const start = addDays(first, -lead);
    const cells: IsoDate[] = [];
    for (let d = start; d <= last || cells.length % 7 !== 0; d = addDays(d, 1)) {
        cells.push(d);
        if (cells.length >= 42) {
            break;
        }
    }
    return cells;
}

function BookingCard({ booking, showHours }: { booking: DayBooking; showHours: boolean }): React.JSX.Element {
    const theme = useTheme();
    const accent = booking.categoryName !== null ? ACCENTS.purple : ACCENTS.blue;
    const title =
        booking.categoryName ??
        (booking.siteAddress !== null && booking.siteAddress !== ''
            ? booking.siteAddress
            : `Job ${booking.jobNumber ?? ''}`);

    return (
        <Paper
            sx={{
                p: 1.75,
                borderRadius: 2,
                border: '1px solid',
                borderColor: alpha(accent, 0.35),
                bgcolor: 'background.paper',
            }}
        >
            <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={1}>
                <Box sx={{ minWidth: 0 }}>
                    {booking.clientName !== null ? (
                        <Typography
                            variant="caption"
                            sx={{ textTransform: 'uppercase', letterSpacing: 0.4, color: 'text.secondary' }}
                        >
                            {booking.clientName}
                        </Typography>
                    ) : null}
                    {/* No job number line: field staff know the site by its
                        address, same as the home screen's cards. */}
                    <Typography sx={{ fontWeight: 700, lineHeight: 1.25 }}>{title}</Typography>
                </Box>
                <Stack direction="row" alignItems="flex-start" spacing={1} sx={{ flexShrink: 0 }}>
                    {showHours ? (
                        <Chip
                            size="small"
                            label={booking.myHours === null ? 'No hours' : `${booking.myHours.toFixed(2)} h`}
                            sx={{
                                bgcolor: alpha(ACCENTS.green, 0.18),
                                color: ACCENTS.green,
                                fontWeight: 700,
                                flexShrink: 0,
                            }}
                        />
                    ) : null}
                    {booking.categoryName === null ? <MapLink address={booking.siteAddress} /> : null}
                </Stack>
            </Stack>

            {booking.startTime !== null || booking.endTime !== null ? (
                <Chip
                    size="small"
                    label={`${booking.startTime?.slice(0, 5) ?? '—'} – ${booking.endTime?.slice(0, 5) ?? '—'}`}
                    sx={{ mt: 1, bgcolor: alpha(accent, 0.15), color: accent, fontWeight: 600 }}
                />
            ) : null}

            {/* The crew. The one thing everybody opens this screen for. */}
            {booking.crew.length > 0 ? (
                <Stack spacing={0.75} sx={{ mt: 1.25 }}>
                    {booking.crew.map((member) => {
                        // SOLID fill, exactly as the office board draws it
                        // (components/calendar/JobGrid.tsx). staff_title.colour_text
                        // is picked to contrast with colour_rectangle AS A SOLID
                        // FILL — black on a pale blue chip, say. Tinting the
                        // background to 12% over a dark surface and keeping that
                        // text colour leaves black on near-black. Same fill means
                        // the same person also reads the same on both screens,
                        // which is the whole point of colouring by skillset.
                        //
                        // readableInk keeps the office's stored pairing unless it
                        // genuinely fails contrast — which it does on two of the
                        // twelve skillsets. See lib/theme.ts.
                        const fill = member.colour ?? accent;
                        const ink = readableInk(fill, member.textColour, theme.palette.getContrastText(fill));
                        return (
                            <Box
                                key={member.staffId}
                                sx={{
                                    px: 1.25,
                                    py: 0.75,
                                    borderRadius: 1,
                                    bgcolor: fill,
                                    // `isMe` can no longer be a heavier tint, so it
                                    // is a ring in the card's own accent instead.
                                    outline: member.isMe ? `2px solid ${accent}` : 'none',
                                    outlineOffset: member.isMe ? '1px' : 0,
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 1,
                                }}
                            >
                                <Typography
                                    sx={{
                                        fontWeight: member.isMe ? 800 : 600,
                                        fontSize: '0.86rem',
                                        color: ink,
                                        flex: 1,
                                        minWidth: 0,
                                    }}
                                    noWrap
                                >
                                    {member.name}
                                    {member.isMe ? ' (you)' : ''}
                                </Typography>
                            </Box>
                        );
                    })}
                </Stack>
            ) : null}

            {booking.notes.map((note) => (
                <Box
                    key={note.id}
                    sx={{
                        mt: 1.25,
                        p: 1.25,
                        borderRadius: 1,
                        bgcolor: alpha(ACCENTS.amber, 0.12),
                        border: '1px solid',
                        borderColor: alpha(ACCENTS.amber, 0.3),
                    }}
                >
                    <Stack direction="row" spacing={0.75} alignItems="center" sx={{ mb: 0.5 }}>
                        <NoteIcon sx={{ fontSize: 15, color: ACCENTS.amber }} />
                        <Typography variant="caption" sx={{ fontWeight: 700, color: ACCENTS.amber }}>
                            Job note
                        </Typography>
                    </Stack>
                    <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                        {note.body}
                    </Typography>
                </Box>
            ))}
        </Paper>
    );
}

export function MyCalendar(): React.JSX.Element {
    const [anchor, setAnchor] = useState<IsoDate>(() => today());
    const [selected, setSelected] = useState<IsoDate>(() => today());
    const [marked, setMarked] = useState<ReadonlySet<IsoDate>>(new Set());
    const [day, setDay] = useState<MyDay | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [view, setView] = useState<'calendar' | 'timesheet'>('calendar');
    const [showToggle, setShowToggle] = useState(false);

    const cells = useMemo(() => monthGrid(anchor), [anchor]);
    const currentMonth = anchor.slice(0, 7);

    // The toggle is a per-staff setting, so it comes from the same payload the
    // home screen reads rather than being guessed at here.
    useEffect(() => {
        fetchHome()
            .then((h) => {
                setShowToggle(h.profile.showTimesheetToggle);
            })
            .catch(() => {
                setShowToggle(false);
            });
    }, []);

    useEffect(() => {
        let active = true;
        const first = cells[0] ?? startOfMonth(anchor);
        const last = cells[cells.length - 1] ?? endOfMonth(anchor);
        fetchMonth(first, last)
            .then((dates) => {
                if (active) {
                    setMarked(new Set(dates));
                }
            })
            .catch(() => {
                if (active) {
                    setMarked(new Set());
                }
            });
        return () => {
            active = false;
        };
    }, [cells, anchor]);

    const loadDay = useCallback(async (date: IsoDate): Promise<void> => {
        try {
            setDay(await fetchDay(date));
            setError(null);
        } catch (err: unknown) {
            setDay(null);
            setError(err instanceof Error ? err.message : String(err));
        }
    }, []);

    useEffect(() => {
        void loadDay(selected);
    }, [selected, loadDay]);

    function stepMonth(direction: -1 | 1): void {
        setAnchor((a) => startOfMonth(addDays(direction === 1 ? endOfMonth(a) : startOfMonth(a), direction)));
    }

    return (
        <EmployeeShell
            title="Calendar"
            subtitle={day?.showingAll === true ? 'Everyone on site' : 'My jobs'}
            backTo="/my"
        >
            {/* ── Month grid ────────────────────────────────────────────── */}
            <Paper sx={{ borderRadius: 3, border: '1px solid', borderColor: 'divider', overflow: 'hidden', mb: 2 }}>
                <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ px: 1, py: 0.5 }}>
                    <IconButton
                        aria-label="Previous month"
                        onClick={() => {
                            stepMonth(-1);
                        }}
                    >
                        <ChevronLeftIcon />
                    </IconButton>
                    <Typography sx={{ fontWeight: 700 }}>{formatMonthYear(anchor)}</Typography>
                    <IconButton
                        aria-label="Next month"
                        onClick={() => {
                            stepMonth(1);
                        }}
                    >
                        <ChevronRightIcon />
                    </IconButton>
                </Stack>

                <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', px: 0.5, pb: 0.5 }}>
                    {WEEKDAYS.map((d) => (
                        <Typography
                            key={d}
                            sx={{
                                textAlign: 'center',
                                fontSize: '0.68rem',
                                fontWeight: 700,
                                textTransform: 'uppercase',
                                color: 'text.secondary',
                                py: 0.5,
                            }}
                        >
                            {d}
                        </Typography>
                    ))}
                    {cells.map((date) => {
                        const inMonth = date.slice(0, 7) === currentMonth;
                        const isSelected = date === selected;
                        const isToday = date === today();
                        const hasWork = marked.has(date);
                        return (
                            <Box
                                key={date}
                                role="button"
                                tabIndex={0}
                                aria-label={formatShort(date)}
                                aria-current={isSelected ? 'date' : undefined}
                                onClick={() => {
                                    setSelected(date);
                                }}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter' || e.key === ' ') {
                                        setSelected(date);
                                    }
                                }}
                                sx={{
                                    // A 44px minimum is the smallest square a
                                    // thumb hits reliably; 42 cells of anything
                                    // smaller is a grid people mis-tap.
                                    minHeight: 44,
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    cursor: 'pointer',
                                    borderRadius: 1.5,
                                    opacity: inMonth ? 1 : 0.35,
                                    bgcolor: isSelected ? alpha(ACCENTS.blue, 0.28) : 'transparent',
                                    border: isToday
                                        ? `1.5px solid ${alpha(ACCENTS.blue, 0.8)}`
                                        : '1.5px solid transparent',
                                    '&:hover': { bgcolor: alpha(ACCENTS.blue, isSelected ? 0.3 : 0.12) },
                                }}
                            >
                                <Typography sx={{ fontSize: '0.9rem', fontWeight: isSelected ? 800 : 500 }}>
                                    {Number(date.slice(8, 10))}
                                </Typography>
                                <Box
                                    sx={{
                                        width: 5,
                                        height: 5,
                                        mt: 0.25,
                                        borderRadius: '50%',
                                        bgcolor: hasWork ? ACCENTS.green : 'transparent',
                                    }}
                                />
                            </Box>
                        );
                    })}
                </Box>
            </Paper>

            {/* ── The day ───────────────────────────────────────────────── */}
            <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1.5 }}>
                <Typography sx={{ fontWeight: 700 }}>{formatShort(selected)}</Typography>
                {showToggle ? (
                    <ToggleButtonGroup
                        size="small"
                        exclusive
                        value={view}
                        onChange={(_e, v: 'calendar' | 'timesheet' | null) => {
                            if (v !== null) {
                                setView(v);
                            }
                        }}
                    >
                        <ToggleButton value="calendar" sx={{ textTransform: 'none', px: 1.5 }}>
                            Calendar
                        </ToggleButton>
                        <ToggleButton value="timesheet" sx={{ textTransform: 'none', px: 1.5 }}>
                            Timesheet
                        </ToggleButton>
                    </ToggleButtonGroup>
                ) : day !== null && day.hoursToday > 0 ? (
                    <Chip
                        size="small"
                        label={`${day.hoursToday.toFixed(2)} h`}
                        sx={{ bgcolor: alpha(ACCENTS.green, 0.18), color: ACCENTS.green, fontWeight: 700 }}
                    />
                ) : null}
            </Stack>

            {error !== null ? (
                <Typography color="error">{error}</Typography>
            ) : day === null ? null : day.bookings.length === 0 ? (
                <Paper
                    sx={{
                        p: 3,
                        borderRadius: 2,
                        border: '1px dashed',
                        borderColor: 'divider',
                        textAlign: 'center',
                        bgcolor: 'transparent',
                    }}
                >
                    <Typography color="text.secondary">Nothing booked for this day</Typography>
                </Paper>
            ) : (
                <Stack spacing={1.5}>
                    {day.bookings.map((booking) => (
                        <BookingCard
                            key={booking.eventId}
                            booking={booking}
                            showHours={showToggle && view === 'timesheet'}
                        />
                    ))}
                </Stack>
            )}

            {/* Notes for the day that belong to no particular job — the legacy
                "Staff Notes" box, which read calendar_staff_notes. */}
            {day !== null && day.generalNotes.length > 0 ? (
                <Stack spacing={1} sx={{ mt: 2 }}>
                    {day.generalNotes.map((note) => (
                        <Paper
                            key={note.id}
                            sx={{
                                p: 1.5,
                                borderRadius: 2,
                                bgcolor: alpha(ACCENTS.green, 0.1),
                                border: '1px solid',
                                borderColor: alpha(ACCENTS.green, 0.3),
                            }}
                        >
                            <Stack direction="row" spacing={0.75} alignItems="center" sx={{ mb: 0.5 }}>
                                <NoteIcon sx={{ fontSize: 15, color: ACCENTS.green }} />
                                <Typography variant="caption" sx={{ fontWeight: 700, color: ACCENTS.green }}>
                                    Staff note
                                </Typography>
                            </Stack>
                            <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                                {note.body}
                            </Typography>
                        </Paper>
                    ))}
                </Stack>
            ) : null}
        </EmployeeShell>
    );
}
